// @flow
import 'isomorphic-fetch';
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import Cookies from 'universal-cookie';
import withAnalytics from 'shared/hoc/withAnalytics';
import validate from 'shared/lib/validate';
import isIPAllowed from 'shared/lib/isIPAllowed';
import { COUNTRY_NAMES } from 'shared/country-names';
import InputField from './InputField';
import InputFieldEvent from './InputFieldEvent';
import Checkbox from './Checkbox';
import DropDown from './DropDown';
import { getAPIDomain, getHideSubscribeFlag } from 'shared/lib/clientSettings';
import Icon from '@ff/ui/Icon';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type Props = {
  classes: Object,
  color_theme: string,
  scroll_in_animation: boolean,
  region: string,
  expandable: Boolean,
  form_fields: Object,
  redirect_link: string,
  title: string,
  always_show_widget: boolean
};

class Subscribe extends React.Component {
  props: Props = this.props;

  state = {
    expanded: false,
    email: '',
    reserveInterest: false,
    firstName: '',
    lastName: '',
    zipcode: '',
    country: '',
    hideZipcode: false,
    textAnimated: false,
    showRedirect: false,
    submitSuccess: false,
    isSubmitting: false,
    disableSubmit: false,
    blockedAreas: false,
    errors: {
      email: false,
      country: false,
      firstName: false,
      lastName: false,
      zipcode: false,
      submit: false
    }
  };

  fieldValidators = {
    email: ['isEmpty', 'isValidEmail'],
    firstName: ['isEmpty'],
    lastName: ['isEmpty'],
    zipcode: ['isEmpty', 'isValidUSZip'],
    country: ['isEmpty']
  }

  container = React.createRef();

  handleExpand = (newVal) => {
    if (this.props.expandable) {
      this.setState({
        reserveInterest: newVal,
        expanded: !this.state.expanded
      });
    } else {
      this.setState({ reserveInterest: newVal });
    }
  }

  componentDidMount = () => {
    if (this.props.scroll_in_animation) {
      document.addEventListener('scroll', this.handleScroll);
      this.animationShouldPlay();
    }

    // Detect client's ip so that we could block users from outside CN/US\
    isIPAllowed().then((value) => {
      if (value === false) {
        this.setState({
          blockedAreas: true
        });
      }
    });
  }

  componentWillUnmount = () => {
    if (this.props.scroll_in_animation) {
      document.removeEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll = () => {
    this.animationShouldPlay();
  }

  animationShouldPlay = () => {
    const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    if (this.container.current) {
      const textNeedsToAnimate = this.container.current.getBoundingClientRect().top < viewportHeight &&
        !this.state.textAnimated;

      if (textNeedsToAnimate) {
        this.setState({
          textAnimated: true
        });
      }
    }
  }

  handleValidate = (field) => {
    const errors = [];
    const validators = this.fieldValidators[field];

    validators.forEach((validator) => {
      const error = validate[validator](this.state[field], this.props.region);

      if (error) {
        errors.push(error);
        this.props.analytics.trackUIEventError('Subscribe Form Error', error);
      }
    });

    this.updateErrors(errors, field);

    return errors.length === 0;
  }

  updateErrors = (errors, field) => {
    // Only show one error at a time.
    this.setState({
      errors: {
        ...this.state.errors,
        [field]: (errors.length > 0 ? errors[0] : false)
      }
    });
  }

  handleErrors = (res) => {
    let errorMessage = '';

    // Catch error thrown by Core-API
    if (res.errorCode === 20) {
      this.updateErrors([res.message], 'submit');
      errorMessage = res.message;
    }
    if (res.errors && res.errors.email) {
      this.updateErrors([res.errors.email], 'email');
      errorMessage = res.errors.email;
    }

    this.props.analytics.trackUIEventError('Subscribe Form Error', errorMessage);
  }

  handleChange = (field, value) => {
    const newState = this.state;

    newState[field] = value;
    this.setState(newState, () => {
      // Validate to see if the field is empty.
      const error = validate.isEmpty(this.state[field], this.props.region);

      if (error) {
        this.updateErrors([error], field);
      } else {
        this.updateErrors([], field);
      }
    });

    // When the country option is changed, we need to update the
    // UI to add/remove fields for different regions by updating state.
    if (field === 'country') {
      if (value === 'United States') {
        this.setState({
          hideZipcode: false,
          showRedirect: false
        });
      } else if (value === 'China') {
        this.setState({
          showRedirect: true
        });
      } else {
        this.setState({
          hideZipcode: true,
          showRedirect: false
        });
      }
    }
  }

  validateBeforeSubmit = (fields) => {
    const errors = {};

    fields.forEach((field) => {
      const validators = this.fieldValidators[field];

      validators.forEach((validator) => {
        const error = validate[validator](this.state[field], this.props.region);

        if (error && !errors[field]) {
          errors[field] = error;
        }
      });
    });
    this.setState({
      errors
    });
    return Object.keys(errors).length === 0;
  }

  handleSubmit = () => {
    const {
      email, reserveInterest, expanded, country, firstName, lastName,
      zipcode, hideZipcode, showRedirect, submitSuccess
    } = this.state;

    if (showRedirect) {
      window.location.href = this.props.redirect_link;
    }

    if (submitSuccess) {
      window.location.href = this.props.redirect_link;
    }

    // Check if errors still exist for fields and prepare for submit.
    if (!expanded && this.validateBeforeSubmit(['email'])) {
      const data = {
        email,
        reserveInterest
      };

      this.submitSubscribe(data);
    }
    if (expanded && !hideZipcode &&
      this.validateBeforeSubmit(['email', 'country', 'firstName', 'lastName', 'zipcode'])) {
      const data = {
        email,
        country,
        firstName,
        lastName,
        reserveInterest,
        zipcode
      };

      this.submitSubscribe(data);
    }
    if (expanded && hideZipcode &&
      this.validateBeforeSubmit(['email', 'country', 'firstName', 'lastName'])) {
      const data = {
        email,
        country,
        firstName,
        lastName,
        reserveInterest,
      };

      this.submitSubscribe(data);
    }
  }

  // This is a temporary solution to handle different data structure
  // and naming conventions when we have the old Django app and core-api
  // co-exist in our web structure. Once the revamped CN reservation app
  // group is available, we will use handleSubmit for both regions.
  handleSubmitToCN = () => {
    const {
      email, reserveInterest, expanded, country, firstName, lastName,
      zipcode, hideZipcode, showRedirect, submitSuccess
    } = this.state;

    if (showRedirect) {
      window.location.href = this.props.redirect_link;
    }

    if (submitSuccess) {
      window.location.href = this.props.redirect_link;
    }

    // Check if errors still exist for fields and prepare for submit.
    if (!expanded && this.validateBeforeSubmit(['email'])) {
      const data = {
        email,
        'reservation_interest': reserveInterest
      };

      this.submitSubscribe(data);
    }
    if (expanded && !hideZipcode &&
      this.validateBeforeSubmit(['email', 'country', 'firstName', 'lastName', 'zipcode'])) {
      const data = {
        email,
        country,
        'first_name': firstName,
        'last_name': lastName,
        'reservation_interest': reserveInterest,
        'postal_code': zipcode
      };

      this.submitSubscribe(data);
    }
    if (expanded && hideZipcode &&
      this.validateBeforeSubmit(['email', 'country', 'firstName', 'lastName'])) {
      const data = {
        email,
        country,
        'first_name': firstName,
        'last_name': lastName,
        'reservation_interest': reserveInterest,
      };

      this.submitSubscribe(data);
    }
  }

  submitSubscribe = (data) => {
    const { region } = this.props;
    const appDomain = getAPIDomain(region) || '';

    let endpointUrl;

    this.setState({ isSubmitting: true });
    if (this.props.region === 'CN') {
      endpointUrl = `${appDomain}/api/subscribe`;
    } else {
      const cookies = new Cookies();

      data.gaCookies = cookies.get('_ga') || '';
      endpointUrl = `${appDomain}/api/subscribe`;
    }
    fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(res => res.json())
      .then((res) => {
        if (res.result === 'success' || res.success) {
          this.setState({
            submitSuccess: true,
            isSubmitting: false
          });
          if (this.props.region === 'US') {
            // Analytics
            const label = data.reserveInterest ? 'with' : 'without';

            this.props.analytics.trackUIEvent('Form Success (Subscribe)', `Success ${label} FF 91 Interest`);
          }
        } else {
          this.setState({
            isSubmitting: false,
            errors: {
              ...this.state.errors,
              submit: `${this.props.form_fields.error_msg}`
            }
          });
          this.handleErrors(res);
        }
      })
      .catch(() => {
        this.setState({
          isSubmitting: false,
          errors: {
            ...this.state.errors,
            submit: `${this.props.form_fields.error_msg}`
          }
        });
      });
  }

  /**
   * Determines if this widget should be hidden, by checking the configured
   * region in the widget and the path of the page.
   *
   * Some CN pages mistakenly use the US widget
   */
  isSubscribeHidden = () => {
    const { region, path, always_show_widget } = this.props;

    let urlRegion;

    if (typeof path !== 'undefined' && path) { // Required for correct server rendering
      urlRegion = path.split('/')[1].toUpperCase();
    }

    // Override the flag if enabled in props
    if (always_show_widget) return false;

    return getHideSubscribeFlag(region || urlRegion) === 'true';
  }

  render() {
    const {
      classes: c,
      color_theme: colorTheme,
      title,
      subtitle,
      interested_in_reserve_checkbox,
      region,
      form_fields,
      isEditing = false,
      useEventStyle,
    } = this.props;
    const {
      email, reserveInterest, expanded, country, firstName, lastName, zipcode,
      textAnimated, errors, hideZipcode, showRedirect, submitSuccess, isSubmitting, disableSubmit, blockedAreas
    } = this.state;

    if (blockedAreas) { return null; }

    if (this.isSubscribeHidden() && !isEditing) {
      return null;
    }

    const reserveCheckbox = (
      <div className={c.checkboxContainer}>
        <Checkbox
          value={reserveInterest}
          label={form_fields && form_fields.interested_in_reserve}
          onChange={newVal => this.handleExpand(newVal)}
        />
      </div>
    );

    const zipcodeField = !hideZipcode && (
      <div className={c.inputContainer}>
        <InputField
          colorTheme={colorTheme}
          placeholder={`${form_fields && form_fields.zipcode}*`}
          value={zipcode}
          onChange={e => this.handleChange('zipcode', e.target.value)}
          onBlur={() => this.handleValidate('zipcode')}
          error={errors.zipcode}
        />
      </div>
    );

    const longForm = (
      <div
        className={classNames(c.longForm, expanded && c.expandedForm,
          expanded && hideZipcode && c.noZipForm)}
      >
        <div className={c.inputContainer}>
          <DropDown
            colorTheme={colorTheme}
            value={country}
            options={COUNTRY_NAMES}
            onChange={newVal => this.handleChange('country', newVal)}
            error={errors.country}
          />
        </div>
        <div className={c.inputContainer}>
          <InputField
            colorTheme={colorTheme}
            placeholder={`${form_fields && form_fields.first_name}*`}
            value={firstName}
            onChange={e => this.handleChange('firstName', e.target.value)}
            onBlur={() => this.handleValidate('firstName')}
            error={errors.firstName}
          />
        </div>
        <div className={c.inputContainer}>
          <InputField
            colorTheme={colorTheme}
            placeholder={`${form_fields && form_fields.last_name}*`}
            value={lastName}
            onChange={e => this.handleChange('lastName', e.target.value)}
            onBlur={() => this.handleValidate('lastName')}
            error={errors.lastName}
          />
        </div>
        {zipcodeField}
      </div>
    );

    const form = !showRedirect && !submitSuccess && (
      <div>
        <div className={c.inputContainer}>
          {useEventStyle ? (
            <InputFieldEvent
              colorTheme={colorTheme}
              placeholder={form_fields && form_fields.email}
              value={email}
              onChange={(e) => this.handleChange('email', e.target.value)}
              onBlur={() => this.handleValidate('email')}
              error={errors.email}
            />
          ) : (
            <InputField
              colorTheme={colorTheme}
              placeholder={form_fields && form_fields.email}
              value={email}
              onChange={(e) => this.handleChange('email', e.target.value)}
              onBlur={() => this.handleValidate('email')}
              error={errors.email}
            />
          )}
        </div>
        {longForm}
        {interested_in_reserve_checkbox && reserveCheckbox}
      </div>
    );

    const redirect = showRedirect && (
      <div>
        <div className={c.redirectText}>{form_fields && form_fields.subscribe_in_china}</div>
      </div>
    );

    const submitError = errors.submit && (
      <span className={c.errorMsg}>
        {errors.submit}
      </span>
    );

    const successMessage = submitSuccess && (
      <div id="subscribe_success" className={c.successMsg}>
        <div>{form_fields && form_fields.success_message}</div>
        <div>{form_fields && form_fields.continue_to_reserve}</div>
      </div>
    );
    const ctaText = submitSuccess ? form_fields?.reserve_now : form_fields?.subscribe;
    let btnContent = isSubmitting ? (
      <div className={classNames({
        [c.subscribeBtn]: !useEventStyle,
        [c.eventBtn]: useEventStyle
      })}>
        <div className={c.loadingAnimation} />
      </div>
    ) : (
      <div className={classNames({
        [c.btnContainer]: !useEventStyle,
        [c.btnContainerEvent]: useEventStyle,
      })}>
        <div
          className={classNames({
            [c.subscribeBtn]: !useEventStyle,
            [c.eventBtn]: useEventStyle,
            [c.disabledBtn]: disableSubmit
          })}
          onClick={() => {
            if (region === 'US' && !disableSubmit) {
              this.handleSubmit();
            } else if (!disableSubmit) {
              this.handleSubmitToCN();
            }
          }}
        >
          {ctaText}
          {!useEventStyle && <Icon name="ff-icon-long-arrow-right" className={classNames(c.btnIcon)} />}
        </div>
      </div>
    );

    // don't show button when there is no reserve checkbox after submit success
    if (submitSuccess && !interested_in_reserve_checkbox) {
      btnContent = null;
    }

    return (
      <div
        className={classNames(c.container, { [c.hiddenInfoOverlay]: (isEditing && this.isSubscribeHidden()) })}
        ref={this.container}
      >
        <div
          className={classNames({
            [c.limitWidth]: !useEventStyle,
            [c.limitWidthEvent]: useEventStyle,
          })}
        >
          <div className={c.titleContainer}>
            <div
              className={classNames(
                {
                  [c.title]: !useEventStyle,
                  [c.titleEvent]: useEventStyle,
                },
                textAnimated && c.animatedText
              )}
            >
              {title}
            </div>
            <div className={classNames(c.subtitle, textAnimated && c.animatedText)}>
              {subtitle}
            </div>
          </div>
          <div className={classNames({ [c.formContainer]: !useEventStyle, [c.formContainerEvent]: useEventStyle })}>
            <div className={c.form}>
              {form}
              {redirect}
              {successMessage}
              {submitError}
            </div>
            {btnContent}
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  container: {
    backgroundColor: (props) => {
      if (props.color_theme === 'DARK') {
        return props.useEventStyle ? '#3f3f43' : '#191919';
      }
      return 'white';
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
  },
  hiddenInfoOverlay: {
    '&:after': {
      content: '"This widget will be hidden from view."',
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      bottom: '0',
      backgroundColor: '#141618',
      opacity: '0.85',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '26px',
    },
  },
  limitWidth: {
    width: '100%',
    maxWidth: '1596px',
    padding: '72px 36px',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'flex-start',
    alignItems: 'flex-end',
    fontFamily: FONT_FAMILY_ROBOTO,
    color: (props) => {
      if (props.color_theme === 'DARK') {
        return 'white';
      }
      return 'black';
    },
    '@media(max-width: 960px)': {
      flexWrap: 'wrap',
      flexDirection: 'column',
      flex: '0 0 100%',
      alignItems: 'flex-start',
    },
  },
  limitWidthEvent: {
    width: '100%',
    maxWidth: '1596px',
    padding: '36px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'flex-start',
    alignItems: 'center',
    fontFamily: FONT_FAMILY_ROBOTO,
    color: (props) => {
      if (props.color_theme === 'DARK') {
        return 'white';
      }
      return 'black';
    },
    '@media(max-width: 960px)': {
      flexWrap: 'wrap',
      flexDirection: 'column',
      flex: '0 0 100%',
      alignItems: 'flex-start',
    },
  },
  subscribeBtn: {
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '600',
    letterSpacing: '1.2px',
    padding: '9px 31px',
    fontSize: '12px',
    border: '1px solid black',
    backgroundColor: 'black',
    color: 'white',
    lineHeight: '28px',
    transition: 'all .2s ease',
    cursor: 'pointer',
    height: '48px',
    width: '177px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'black',
    },
    '@media(max-width: 960px)': {
      width: '100%',
      textAlign: 'center',
    },
  },
  eventBtn: {
    width: '264px',
    height: '38px',
    // backgroundColor: '#ffffff33',
    color: 'white',
    border: '1px solid #ffffffa6',
    borderRadius: '4px',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '400',
    letterSpacing: 0,
    fontSize: '14px',
    lineHeight: '28px',
    transition: 'all .2s ease',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#ffffff33',
    },
  },
  disabledBtn: {
    backgroundColor: 'hsl(16, 0%, 45%)',
    cursor: 'not-allowed',
    border: '1px solid hsl(16, 0%, 45%)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'hsl(16, 0%, 45%)',
      color: 'white',
    },
  },
  btnIcon: {
    marginLeft: '5px',
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: '30px',
    fontWeight: '300',
    letterSpacing: '3px',
    marginBottom: '24px',
    flexGrow: '1',
    flexBasis: '0',
    opacity: (props) => {
      if (props.scroll_in_animation) {
        return '0';
      }
      return '1';
    },
    transform: (props) => {
      if (props.scroll_in_animation) {
        return 'translateY(130%)';
      }
      return 'none';
    },
    transition: 'opacity .5s ease, transform .5s ease',
    '@media(max-width: 960px)': {
      width: '100%',
    },
  },
  titleEvent: {
    fontSize: '20px',
    fontWeight: '300',
    letterSpacing: '1px',
    marginBottom: 0,
    flexGrow: '1',
    flexBasis: '0',
    lineHeight: '170%',
    paddingBottom: '20px',
    opacity: (props) => {
      if (props.scroll_in_animation) {
        return '0';
      }
      return '1';
    },
    transform: (props) => {
      if (props.scroll_in_animation) {
        return 'translateY(130%)';
      }
      return 'none';
    },
    transition: 'opacity .5s ease, transform .5s ease',
    '@media(max-width: 960px)': {
      paddingBottom: 0,
      width: '100%',
    },
  },
  subtitle: {
    fontSize: '20px',
    letterSpacing: '2px',
  },
  animatedText: {
    transform: 'translateY(0) !important',
    opacity: '1 !important',
  },
  formContainer: {
    display: 'flex',
    flex: 2,
    '@media(max-width: 960px)': {
      flexDirection: 'column',
      width: '100%',
    },
  },
  formContainerEvent: {
    display: 'flex',
    flex: 1.5,
    // prevent layout shift
    minHeight: '82px',
    alignItems: 'start',
    '@media(max-width: 960px)': {
      // prevent layout shift
      minHeight: '166px',
      flexDirection: 'column',
      width: '100%',
    },
  },
  form: {
    flex: 2,
    paddingLeft: '10px',
    paddingRight: '10px',
    '@media(max-width: 960px)': {
      width: '100%',
      marginTop: '36px',
      padding: '0',
    },
  },
  btnContainer: {
    display: 'flex',
    paddingLeft: '35px',
    justifyContent: 'flex-end',
    '@media(max-width: 960px)': {
      width: '100%',
      marginTop: '36px',
      justifyContent: 'center',
      display: 'flex',
      padding: '0',
    },
  },
  btnContainerEvent: {
    display: 'flex',
    paddingLeft: '20px',
    justifyContent: 'flex-end',
    marginTop: '10px',
    '@media(max-width: 960px)': {
      width: '100%',
      marginTop: '10px',
      justifyContent: 'center',
      display: 'flex',
      padding: '0',
    },
  },
  longForm: {
    height: '0',
    overflow: 'hidden',
    transition: 'all .25s ease-in-out',
  },
  expandedForm: {
    height: '348px',
  },
  noZipForm: {
    height: '246px',
  },
  inputContainer: {
    paddingBottom: '12px',
    height: '82px',
  },
  redirectText: {
    fontSize: '18px',
    paddingLeft: '18px',
    paddingTop: '18px',
    '@media(max-width: 960px)': {
      paddingLeft: '0',
    },
  },
  successMsg: {
    fontSize: '16px',
    fontWeight: '300',
    lineHeight: '20px',
    '&> div': {
      marginBottom: '15px',
    },
  },
  errorMsg: {
    color: 'hsl(360, 100%, 40%)',
    fontSize: '16px',
    display: 'block',
    marginTop: '5px',
  },
  loadingAnimation: {
    fontSize: '0',
    width: '20px',
    height: '20px',
    marginTop: '5px',
    borderRadius: '15px',
    padding: '0',
    border: '3px solid #FFFFFF',
    borderBottom: '3px solid rgba(255,255,255,0.0)',
    borderLeft: '3px solid rgba(255,255,255,0.0)',
    backgroundColor: 'transparent !important',
    animationName: '$rotateAnimation',
    animationDuration: '1s',
    animationDelay: '0.2s',
    animationIterationCount: 'infinite',
  },
  '@keyframes rotateAnimation': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },
};

export default withAnalytics(injectSheet(styles)(Subscribe));
